import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { reportError } from '@/lib/error-reporting';
import { isVersionNewer } from '@/lib/version-compare';

export function useUnreadChangelog() {
  const { user } = useAuth();
  const { profile, invalidate: invalidateProfile } = useProfile();

  const { data: latestVersion } = useQuery<string | null>({
    queryKey: ['changelog-latest-version'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('changelog_versions')
        .select('version')
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data?.version ?? null;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const lastSeen = profile?.last_seen_changelog_version ?? null;
  const hasUnread = !!latestVersion && isVersionNewer(latestVersion, lastSeen);

  const markAsSeen = async () => {
    if (!user || !latestVersion || !hasUnread) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ last_seen_changelog_version: latestVersion })
        .eq('user_id', user.id);

      if (error) throw error;
      await invalidateProfile();
    } catch (err) {
      reportError(err, 'changelog', { action: 'mark-as-seen' });
    }
  };

  return { hasUnread, latestVersion: latestVersion ?? null, markAsSeen };
}
